<template>
	<Modal
		:show="showCreateAppointment"
		content_class="w-3/4 max-w-3xl mx-auto"
	>
		<template slot="content">
			<div
				class="relative w-full p-6 sm:p-8 bg-gradient-to-b from-gray-50 to-white"
			>
				<!-- Close Button -->
				<button
					class="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-all duration-300 hover:rotate-90"
					@click="closeModal"
				>
					<i class="fa fa-times text-xl"></i>
				</button>

				<!-- Title -->
				<h1
					class="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-6"
				>
					Book Appointment
				</h1>

				<!-- Intermediaries Section -->
				<div
					class="bg-white/90 backdrop-blur-md rounded-2xl p-6 border border-gray-100 shadow-lg mb-6"
				>
					<div class="flex items-center justify-between mb-4">
						<div class="flex items-center gap-2">
							<svg
								class="w-5 h-5 text-indigo-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
								></path>
							</svg>
							<h3 class="text-lg font-semibold text-gray-900">
								Select Intermediaries
							</h3>
						</div>
						<!-- <span class="px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full">
              {{ selectedIntermediaries.length }} selected
            </span> -->
					</div>

					<!-- Search Input -->
					<div class="mb-4">
						<input
							type="search"
							v-model="search"
							class="w-full px-4 py-3 rounded-full border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all bg-white/50 placeholder-gray-400"
							placeholder="Search by email or name..."
						/>
					</div>

					<!-- Intermediaries Grid -->
					<div
						class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto custom-scrollbar"
					>
						<div
							v-for="intermediary in filteredIntermediaries()"
							v-if="intermediary.status === 'ACTIVE'"
							:key="intermediary.id"
							@click="
								toggleIntermediary(
									intermediary.intermediarySystemRef,
								)
							"
							class="relative p-4 bg-white rounded-xl border border-gray-100 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
							:class="{
								'border-indigo-500 shadow-md ring-2 ring-indigo-100 bg-indigo-50/30':
									selectedIntermediary.includes(
										intermediary.intermediarySystemRef,
									),
								'hover:border-indigo-200':
									!selectedIntermediary.includes(
										intermediary.intermediarySystemRef,
									),
							}"
						>
							<!-- Checkbox -->
							<div class="absolute top-3 right-3">
								<div
									class="w-5 h-5 rounded-full border flex items-center justify-center transition-all"
									:class="{
										'bg-indigo-600 border-indigo-600 text-white':
											selectedIntermediary.includes(
												intermediary.intermediarySystemRef,
											),
										'border-gray-300 group-hover:border-indigo-400':
											!selectedIntermediary.includes(
												intermediary.intermediarySystemRef,
											),
									}"
								>
									<!-- <svg v-if="selectedIntermediaries.includes(Number(intermediary.id))" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                  </svg> -->
								</div>
							</div>

							<!-- Profile Image -->
							<div class="flex justify-center mb-3">
								<div
									class="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-md"
								>
									<img
										:src="
											intermediary.profileImage ||
											'/assets/Logo/nexus.svg'
										"
										:alt="
											intermediary.intermediaryNames
												? `${intermediary.intermediaryNames} Logo`
												: 'Default Logo'
										"
										class="w-full h-full object-cover"
									/>
								</div>
							</div>

							<!-- Intermediary Info -->
							<div class="text-center">
								<h4
									class="text-sm font-semibold text-gray-900 truncate mb-1"
								>
									{{ intermediary.intermediaryNames }}
								</h4>
								<span
									class="inline-block px-2 py-1 text-xs font-medium rounded-full"
									:class="{
										'bg-indigo-100 text-indigo-700':
											intermediary.category === 'broker',
										'bg-green-100 text-green-700':
											intermediary.category !== 'broker',
									}"
								>
									{{ intermediary.category }}
								</span>
							</div>
						</div>
					</div>
					<p
						v-if="errors.selectedIntermediary"
						class="text-sm text-red-500 mt-2"
					>
						{{ errors.selectedIntermediaries }}
					</p>
				</div>
				<div
					class="bg-white/90 backdrop-blur-md rounded-2xl p-6 border border-gray-100 shadow-lg mb-6"
				>
					<div class="flex items-center gap-2 mb-4">
						<svg
							class="w-5 h-5 text-indigo-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
							></path>
						</svg>
						<h3 class="text-lg font-semibold text-gray-900">
							Subject
						</h3>
					</div>
					<input
						v-model="title"
						@blur="validateField('title')"
						class="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all resize-none"
						:class="{ 'border-red-500': errors.description }"
						placeholder="Please provide details about your query or issue..."
					/>
					<p v-if="errors.title" class="text-sm text-red-500 mt-2">
						{{ errors.title }}
					</p>
					<p class="text-sm text-gray-500 mt-2">
						Describe the subject
					</p>
				</div>
				<!-- Appointment Date Section -->
				<div
					class="bg-white/90 backdrop-blur-md rounded-2xl p-6 border border-gray-100 shadow-lg mb-6"
				>
					<div class="flex items-center gap-2 mb-4">
						<svg
							class="w-5 h-5 text-indigo-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
							></path>
						</svg>
						<h3 class="text-lg font-semibold text-gray-900">
							Appointment Date
						</h3>
					</div>
					<input
						id="book_date"
						type="date"
						v-model="book_date"
						@blur="validateField('book_date')"
						:min="todayDate"
						class="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
						:class="{ 'border-red-500': errors.book_date }"
					/>
					<p
						v-if="errors.book_date"
						class="text-sm text-red-500 mt-2"
					>
						{{ errors.book_date }}
					</p>
				</div>
				<!-- Appointment time -->
				<div
					class="bg-white/90 backdrop-blur-md rounded-2xl p-6 border border-gray-100 shadow-lg mb-6"
				>
					<div class="flex items-center gap-2 mb-4">
						<svg
							class="w-5 h-5 text-indigo-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
							></path>
						</svg>
						<h3 class="text-lg font-semibold text-gray-900">
							Appointment Time
						</h3>
					</div>
					<input
						id="book_time"
						type="time"
						v-model="book_time"
						@blur="validateField('book_time')"
						:min="todayDate"
						class="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
						:class="{ 'border-red-500': errors.book_date }"
					/>
					<p
						v-if="errors.book_date"
						class="text-sm text-red-500 mt-2"
					>
						{{ errors.book_time }}
					</p>
				</div>

				<!-- Description Section -->
				<div
					class="bg-white/90 backdrop-blur-md rounded-2xl p-6 border border-gray-100 shadow-lg mb-6"
				>
					<div class="flex items-center gap-2 mb-4">
						<svg
							class="w-5 h-5 text-indigo-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
							></path>
						</svg>
						<h3 class="text-lg font-semibold text-gray-900">
							Describe the Appointment
						</h3>
					</div>
					<textarea
						v-model="description"
						@blur="validateField('description')"
						class="w-full h-32 px-4 py-3 rounded-xl border border-gray-200 bg-white/50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all resize-none"
						:class="{ 'border-red-500': errors.description }"
						placeholder="Please provide details about your query or issue..."
					></textarea>
					<p
						v-if="errors.description"
						class="text-sm text-red-500 mt-2"
					>
						{{ errors.description }}
					</p>
					<p class="text-sm text-gray-500 mt-2">
						Describe your insurance needs or any questions you have
					</p>
				</div>

				<!-- Submit Button -->
				<button
					class="w-full py-3 px-6 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-all duration-300 shadow-md"
					@click="submitAppointment"
				>
					Submit Appointment
				</button>
			</div>
		</template>
	</Modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { bookAppointmentApi, fecthClientAppointments } from '../api/portal';
import { getPageNumbers } from '../../../helpers/pagination';

export default {
	components: {
		Modal: () => import('@/components/newportal/templates/others/modal'),
	},
	data() {
		return {
			search: '',
			book_date: '',
			book_time: '',
			description: '',
			title: '',
			selectedIntermediary: '',
			errors: {
				book_date: '',
				book_time: '',
				description: '',
				title: '',
				selectedIntermediary: '',
			},
		};
	},
	computed: {
		...mapGetters('appointment', ['showCreateAppointment']),
		...mapGetters('help', ['selectEntities']),
		intermediaries() {
			return this.selectEntities;
		},
		todayDate() {
			const today = new Date();
			const year = today.getFullYear();
			const month = String(today.getMonth() + 1).padStart(2, '0');
			const day = String(today.getDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		},
	},
	methods: {
		...mapActions('appointment', [
			'setShowCreateAppointments',
			'setMyAppointments',
		]),
		...mapActions('pagination', [
			'setPagesArray',
			'setTotalPages',
			'setCurrentPage',
		]),
		filteredIntermediaries() {
			const query = this.search ? this.search.toLowerCase().trim() : '';
			if (!query) return this.selectEntities;

			return this.selectEntities.filter((inter) => {
				const names = inter.intermediaryNames
					? inter.intermediaryNames.toLowerCase()
					: '';
				const email = inter.primaryEmail
					? inter.primaryEmail.toLowerCase()
					: '';
				return names.includes(query) || email.includes(query);
			});
		},
		toggleIntermediary(id) {
			// const index = this.selectedIntermediaries.indexOf(id);
			// if (index === -1) {
			//   this.selectedIntermediaries.push(id);
			// } else {
			//   this.selectedIntermediaries.splice(index, 1);
			// }
			// if (this.hasAttemptedSubmit) {
			//   this.validateField('selectedIntermediaries');
			// }
			this.selectedIntermediary = id;
		},
		validateField(field) {
			// Add your validation logic here
			this.errors[field] = '';

			switch (field) {
				case 'book_date':
					if (!this.book_date) {
						this.errors.book_date = 'Book date is required';
					}
					break;
				case 'book_time':
					if (!this.book_time) {
						this.errors.book_time = 'Appointment time is required';
					}
					break;
				case 'selectedIntermediary':
					if (!this.selectedIntermediary) {
						this.errors.selectedIntermediary =
							'Please select an intermediary';
					}
					break;
				case 'description':
					if (!this.description) {
						this.errors.description = 'Description is required';
					}
					break;
				case 'title':
					if (!this.title) {
						this.errors.title = 'Title is required';
					}
					break;
			}
		},
		closeModal() {
			this.setShowCreateAppointments(!this.showCreateAppointment);
		},
		async fetchAppointments() {
			const fetchData = { querryOptions: { page: 1 } };
			try {
				const res = await fecthClientAppointments(fetchData);
				if (res.status) {
					this.setMyAppointments(res.data[0].items);
					const pages = getPageNumbers(res.data[0].pageCount, 20);
					this.setPagesArray(pages);
					this.setTotalPages(res.data[0].totalCount);
				}
			} catch (err) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: err.message || 'Failed to process payment',
					duration: 3000,
				});
			}
		},
		appointmentData() {
			return {
				intermediarySystemRef: this.selectedIntermediary,
				appointmentDate: this.book_date,
				appointmentTime: this.book_time,
				message: this.description,
				title: this.title,
			};
		},
		async submitAppointment() {
			try {
				const res = await bookAppointmentApi(this.appointmentData());
				if (res.status) {
					this.setShowCreateAppointments(!this.showCreateAppointment);
					await this.fetchAppointments();
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Success',
						message: res.data,
						duration: 3000,
					});
				}
			} catch (err) {
				console.error('Error processing payment:', err);
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: err.message || 'Failed to process payment',
					duration: 3000,
				});
			}
		},
	},
	mounted() {
		console.log('modal-open', this.showCreateAppointment);
	},
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
	width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
	background: #555;
}
</style>
